import { useRef, useState, useCallback, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import useToaster from "hooks/useToaster";

import { QuickFilter } from "../components";
import { Filter, TableView, UploadFilesView } from "./components";
import { ButtonV1, ModalBox, UploadCSV, DropdownV1 } from "components";

import { postCall_v2 } from "services";
import { getInventoryPayload } from "./DataModel";

import { INVERNTORY_QUICK_FILTERS, SHIPMENT_REPORT_TYPES } from "../constant";
import { JOMS_API_BASE_V1_URL, success, error } from "constants";
import { validateErrorMsg } from "utils";

const ROWS_PER_PAGE = 50;
/**
 * @description Inventory component for managing bulk uploads and filtering
 * @returns {JSX.Element} The rendered component
 */
const FinishedGoods = () => {
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [page, setPage] = useState(0);
  const [tableData, setTableData] = useState({});
  const [filterValues, setFilterValues] = useState({});
  const [uploadResult, setUploadResult] = useState(null);
  const [selectedQuickFilter, setSelectedQuickFilter] = useState("All");
  const [selectedShipmentReport, setSelectedShipmentReport] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [activeModal, setActiveModal] = useState(null);

  const csvUploadRef = useRef();
  const triggerToaster = useToaster();

  // Custom validation function to check if report type is selected
  const validateBeforeUpload = useCallback(() => {
    if (!selectedShipmentReport || selectedShipmentReport === "") {
      triggerToaster("Please select a shipment report type", error);
      return false;
    }
    return true;
  }, [selectedShipmentReport, triggerToaster]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleFilterSubmit = useCallback((values) => {
    if (values) {
      setFilterValues(values);
    } else {
      setFilterValues({});
    }
    setPage(0); // Reset to first page when filters change
  }, []);

  /**
   * @description Handles file upload callback with proper error handling
   * @param {File} file - The uploaded file
   */
  const fileUploadCallback = useCallback(
    async (file) => {
      if (!file) return;
      if (!selectedShipmentReport || selectedShipmentReport === "") {
        triggerToaster("Please select a shipment report type", error);
        return;
      }

      setIsFileUploading(true);
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await postCall_v2(
          `${JOMS_API_BASE_V1_URL}/v1/inventory-data/upload?fileType=${selectedShipmentReport}`,
          formData,
        );
        setUploadResult(res?.data || null);
        setActiveModal("softDelete");
        triggerToaster("File uploaded successfully", success);
        fetchInventoryData();
      } catch (err) {
        triggerToaster(validateErrorMsg(err?.data?.message), error);
        setActiveModal(null);
        setUploadResult(null);
      } finally {
        setIsFileUploading(false);
        csvUploadRef?.current?.resetFile();
      }
    },
    [selectedShipmentReport],
  );

  const onConfirmSoftDelete = async () => {
    try {
      const res = await postCall_v2(
        `${JOMS_API_BASE_V1_URL}/v1/inventory-data/confirm-soft-delete`,
        {
          bulkDoUploadFileId: uploadResult?.doUploadFileRtabRowId,
        },
      );
      if (res?.data?.isSuccess) {
        triggerToaster(
          res?.data?.message || "Soft delete confirmed successfully",
          success,
        );
      } else {
        triggerToaster(validateErrorMsg(res?.data?.message), error);
      }
      setUploadResult(null);
      fetchInventoryData();
      setActiveModal("uploadedFiles");
    } catch (err) {
      triggerToaster(validateErrorMsg(err?.data?.message), error);
      setActiveModal(null);
    }
  };

  const handleRejectUpload = useCallback(() => {
    setActiveModal(null);
    setUploadResult(null);
  }, []);

  const fetchInventoryData = async () => {
    try {
      const response = await postCall_v2(
        `/joms/api/external/v1/inventory-data/fetch-inventory-tab-data`,
        getInventoryPayload(
          page,
          ROWS_PER_PAGE,
          filterValues,
          selectedQuickFilter,
        ),
      );
      setTableData(response?.data || []);
      setIsLoading(false);
    } catch (error) {
      setTableData([]);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchInventoryData();
  }, [filterValues, page, selectedQuickFilter]);

  const handleQuickFilter = (selectedFilter) => {
    setPage(0);
    setSelectedQuickFilter(selectedFilter);
  };

  const handleReset = () => {
    setSelectedQuickFilter("All");
  };

  const onCloseModal = () => {
    setActiveModal(null);
  };

  return (
    <Box>
      <Box>
        {/* bulk upload */}
        <Box mb={8}>
          {/* Header */}
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 4 }}>
            Bulk upload
          </Typography>

          {/* Shipment Report Dropdown */}
          <Box mb={4}>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
              Select shipment report
            </Typography>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <DropdownV1
                placeholder="Select report"
                itemList={SHIPMENT_REPORT_TYPES}
                value={selectedShipmentReport || ""}
                onChange={(e) => setSelectedShipmentReport(e.target.value)}
                sx={{ maxWidth: 300 }}
              />
              <ButtonV1
                title={"Uploaded files"}
                onClick={() => setActiveModal("uploadedFiles")}
                variant="outlined"
              />
            </Box>
          </Box>

          <UploadCSV
            fileCallBackFn={fileUploadCallback}
            allowedMaxSize={2}
            ref={csvUploadRef}
            isLoading={isFileUploading}
            fileType={"xlsx"}
            validateBeforeUpload={validateBeforeUpload}
          />
          <ModalBox
            open={activeModal === "softDelete"}
            width="50%"
            onCloseModal={handleRejectUpload}
          >
            <Box>
              <Typography variant="h5" mb={2} data-testid="soft-delete-header">
                Soft deletion of old records
              </Typography>
              <Box p={4}>{`${
                uploadResult?.softDeleteCount ?? 0
              } number of inventory records are being soft deleted. The new records would be uploaded. All the existing Pre-DOs that are not not released would be rejected, and New DOs would be created based on the inventory sheet uploaded.`}</Box>
            </Box>
            <Box display={"flex"} justifyContent={"center"} gap={2}>
              <ButtonV1 title={"Confirm"} onClick={onConfirmSoftDelete} />
              <ButtonV1
                variant="outlined"
                title={"Close"}
                data-testid="close-modal-btn"
                onClick={handleRejectUpload}
              />
            </Box>
          </ModalBox>
        </Box>
        {/* filter */}
        <Box>
          <Filter onSubmit={handleFilterSubmit} onReset={handleReset} />
        </Box>
        {/* quick filter */}
        <Box mt={8}>
          <QuickFilter
            onQuickFilter={handleQuickFilter}
            state={selectedQuickFilter}
            filterData={INVERNTORY_QUICK_FILTERS}
          />
        </Box>
        {/* inventory table */}
        <Box mt={8}>
          <TableView
            rowsPerPage={ROWS_PER_PAGE}
            page={page}
            onPageChange={handlePageChange}
            data={tableData}
            isLoading={isLoading}
          />
        </Box>
        <ModalBox
          open={activeModal === "uploadedFiles"}
          onCloseModal={onCloseModal}
          width="65%"
        >
          <UploadFilesView onClose={onCloseModal} rowsPerPage={ROWS_PER_PAGE} />
        </ModalBox>
      </Box>
    </Box>
  );
};

export default FinishedGoods;
